import {
  resolveDesenquadramentoStatus,
  buildAnbimaFracoesFromRisk,
  OUTROS_CLASSE_LABEL,
} from '@modules/consolidator/utils/build-desenquadramento-data/build-desenquadramento-data'
import { ONE_DAY_MS } from '@modules/consolidator/utils/wallet-alerts-dismiss/wallet-alerts-dismiss'
import { resolveWalletWarning } from '@modules/consolidator/utils/resolve-wallet-warnings/resolve-wallet-warnings'
import { ANBIMA_STATUS } from '@modules/consolidator/constants/anbima-status'

// Classes que o alerta nomeia como acima do limite. Uma classe SEM teto
// próprio não vira linha na tabela — o backend soma todas contra o teto de
// 'Outros' e carimba o MESMO signal em cada uma delas
// (aawz_base/api/db_dir/anbima/utils_anbima_adherence.py:292-294). Nomeá-las
// aqui manda o usuário procurar na tela linhas que o detalhamento agregou:
// ele lê "Fundo Regional XPTO" e só encontra "Outros". Por isso o rótulo do
// grupo substitui os membros, uma vez só.
/** Id da ação do alerta de sugestões. Literal repetido em 3 arquivos vira drift silencioso. */
export const ACAO_SUGESTOES_IA = 'sugestoes-ia'

export const resolveClassesAcimaLimite = (walletAnalysis) => {
  const fracoes = buildAnbimaFracoesFromRisk(walletAnalysis) || {}
  const classes = []
  Object.keys(fracoes).forEach((classe) => {
    const entry = fracoes[classe]
    if (!entry || entry.signal !== '>') return
    const rotulo = entry.groupedAsOutros ? OUTROS_CLASSE_LABEL : classe
    if (!classes.includes(rotulo)) classes.push(rotulo)
  })
  return classes
}

const buildDesenquadramentoMessage = (classes) => {
  const base = 'Cliente com desenquadramento ANBIMA detectado.'
  if (!classes.length) return base
  return `${base} Classes acima do limite: ${classes.join(', ')}.`
}

const compareByCodeUnit = (a, b) => {
  if (a < b) return -1
  if (a > b) return 1
  return 0
}

const screenApplies = (context, screens) => {
  if (!screens || !context.screen) return true
  return screens.includes(context.screen)
}

const resolveDataDesatualizadaAlert = (context) => {
  if (!screenApplies(context, ['carteira-atual'])) return null
  const active =
    !context.loading && !context.loadingClients && Boolean(context.dataOutdated)
  if (!active) return null
  return {
    id: 'dados-desatualizados',
    dismissKey: 'dados-desatualizados',
    message: 'Dados desatualizados',
    icon: 'warning',
    iconColor: 'warning',
    closable: true,
    actionId: 'atualizar',
    actionLabel: 'Atualizar',
    signature: String(context.dataOutdated),
    ttlMs: ONE_DAY_MS,
  }
}

const resolveDesenquadramentoAlert = (context) => {
  if (context.isFamilyGroup) return null
  const status = resolveDesenquadramentoStatus(context.walletAnalysis)
  if (status !== ANBIMA_STATUS.ACIMA) return null
  const classes = resolveClassesAcimaLimite(context.walletAnalysis)
  return {
    id: 'desenquadramento-anbima',
    dismissKey: 'desenquadramento-anbima',
    message: buildDesenquadramentoMessage(classes),
    icon: 'warning',
    iconColor: 'warning',
    closable: true,
    actionId: '',
    actionLabel: '',
    signature:
      classes.slice().sort(compareByCodeUnit).join('|') || 'acima-do-risco',
    ttlMs: ONE_DAY_MS,
  }
}

// Warnings crus do backend passam pelas WALLET_WARNING_RULES (mensagem
// amigável + ação "cotizar") e são deduplicados pelo texto resolvido —
// regras distintas podem convergir para a mesma mensagem.
const resolveWarningAlerts = (context) => {
  const warnings = context.walletAnalysis && context.walletAnalysis.warnings
  if (!Array.isArray(warnings)) return []
  const seenTexts = []
  const instances = []
  warnings.forEach((warning) => {
    const resolved = resolveWalletWarning(warning)
    if (!resolved.text) return
    if (seenTexts.includes(resolved.text)) return
    seenTexts.push(resolved.text)
    instances.push({
      id: `warning-${instances.length}`,
      dismissKey: `warning:${resolved.text}`,
      message: resolved.text,
      icon: 'warning',
      iconColor: 'warning',
      closable: true,
      actionId: resolved.action || '',
      actionLabel: resolved.actionLabel || '',
      signature: resolved.text,
      ttlMs: ONE_DAY_MS,
    })
  })
  return instances
}

/**
 * Sugestões de IA pendentes entram na MESMA barra dos outros alertas da carteira: é onde o assessor
 * já olha para saber o que exige ação. O selo na linha continua existindo — ele diz ONDE está o
 * problema; o alerta diz QUE existe, sem depender de o olho passar por cima da tabela.
 *
 * `null` (fila não apurada) NÃO gera alerta: afirmar "0 pendentes" sem ter apurado é o mesmo
 * defeito que a contagem do selo evita com o travessão.
 */
const resolveSugestoesIaAlert = (context) => {
  if (context.isFamilyGroup) return null
  const contagem = context.sugestoesPendentesPorAtivo
  if (!contagem) return null
  const ativos = Object.keys(contagem).filter((id) => contagem[id] > 0)
  if (!ativos.length) return null
  const total = ativos.reduce((soma, id) => soma + contagem[id], 0)
  return {
    id: 'sugestoes-ia-pendentes',
    dismissKey: 'sugestoes-ia-pendentes',
    message: buildSugestoesIaMessage({ total, ativos: ativos.length }),
    // `ai-sparkle` e nao `stars`: o `stars` pinta por `pattern` e IGNORA a prop de cor, entao a
    // linha lateral saia azul com o icone escuro. Este usa `fill="color"`, e as duas pontas ficam
    // sempre na MESMA cor — que e o que o BaseAlert assume (a linha e `backgroundColor: colorIcon`).
    icon: 'ai-sparkle',
    iconColor: 'info',
    closable: true,
    actionId: ACAO_SUGESTOES_IA,
    actionLabel: 'Revisar',
    signature: `${total}:${ativos.length}`,
    ttlMs: ONE_DAY_MS,
  }
}

export const buildSugestoesIaMessage = ({ total, ativos }) => {
  const sugestao =
    total === 1
      ? '1 sugestão da IA aguarda'
      : `${total} sugestões da IA aguardam`
  const ativo = ativos === 1 ? '1 ativo' : `${ativos} ativos`
  return `${sugestao} sua curadoria, em ${ativo} desta carteira.`
}

export const resolveWalletAlerts = (context = {}) => {
  if (context.isClient) return []
  const alerts = []
  const outdated = resolveDataDesatualizadaAlert(context)
  if (outdated) alerts.push(outdated)
  const desenquadramento = resolveDesenquadramentoAlert(context)
  if (desenquadramento) alerts.push(desenquadramento)
  const sugestoes = resolveSugestoesIaAlert(context)
  if (sugestoes) alerts.push(sugestoes)
  return alerts.concat(resolveWarningAlerts(context))
}
